// src/features/analytics/mediaPeaks.ts
//
// Pure transforms for the per-media "Peak viewers per day" panel (metrics
// expansion). The API omits days with no data (honest absence); the chart
// zero-fills for axis continuity only — callers clamp the fill window with
// playbackTrend's fillableRange so future and pre-coverage days never get a
// fabricated zero. Zero-filled days carry no peak minute.

import { SECONDS_PER_DAY, type UtcRange } from '@/shared/utils/utcRange';
import type { MediaPeakRow, MediaPeaksData } from './types';

/** One charted UTC day; peakMinute is null on zero-filled (no-data) days. */
export interface PeakChartDay {
  day: number;
  peakConcurrent: number;
  peakMinute: number | null;
  provisional: boolean;
}

/**
 * Zero-fill missing UTC days across an end-exclusive day-aligned range so the
 * axis is continuous. Rows outside the range pass through untouched at their
 * sorted position (defensive; the API only returns in-range days).
 */
export function fillPeakDayGaps(rows: MediaPeakRow[], range: UtcRange): PeakChartDay[] {
  const byDay = new Map(rows.map((r) => [r.day, r]));
  const out: PeakChartDay[] = [];
  for (let day = range.from; day < range.to; day += SECONDS_PER_DAY) {
    const row = byDay.get(day);
    out.push(
      row
        ? {
            day: row.day,
            peakConcurrent: row.peakConcurrent,
            peakMinute: row.peakMinute,
            provisional: row.provisional === true,
          }
        : { day, peakConcurrent: 0, peakMinute: null, provisional: false }
    );
  }
  return out;
}

/**
 * Whether the range-wide peak came from a provisional day. rangePeak itself
 * carries no provisional flag — the source row (matched by day) does.
 */
export function rangePeakProvisional(data: MediaPeaksData): boolean {
  if (data.rangePeak === null) return false;
  const day = data.rangePeak.day;
  return data.rows.find((r) => r.day === day)?.provisional === true;
}
